'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  ArrowRight,
  ArrowUpRight,
  Blocks,
  CheckCircle2,
  CreditCard,
  Handshake,
  Landmark,
  Megaphone,
  MessageCircle,
  PackageSearch,
  Pill,
  Plug,
  Printer,
  QrCode,
  ShieldCheck,
  Shirt,
  ShoppingCart,
  Smartphone,
  Truck,
  UtensilsCrossed,
  type LucideIcon,
} from 'lucide-react';

export type MarketingLandingT = (
  key: string,
  params?: Record<string, string | number>,
) => string;

type MarketingLandingPageProps = {
  t: MarketingLandingT;
  normalizedLocale: string;
};

const TICKER_KEYS = [
  'landing.ticker.i1',
  'landing.ticker.i2',
  'landing.ticker.i3',
  'landing.ticker.i4',
  'landing.ticker.i5',
  'landing.ticker.i6',
  'landing.ticker.i7',
  'landing.ticker.i8',
  'landing.ticker.i9',
  'landing.ticker.i10',
  'landing.ticker.i11',
  'landing.ticker.i12',
] as const;

const LAYERS = [
  { number: '01', titleKey: 'landing.layers.l1Title', descriptionKey: 'landing.layers.l1Description' },
  { number: '02', titleKey: 'landing.layers.l2Title', descriptionKey: 'landing.layers.l2Description' },
  { number: '03', titleKey: 'landing.layers.l3Title', descriptionKey: 'landing.layers.l3Description' },
] as const;

const CARD_ENTRANCE_DELAYS = ['', 'delay-100', 'delay-200', 'delay-100', 'delay-200', 'delay-300'] as const;

const PILLARS: ReadonlyArray<{
  icon: LucideIcon;
  titleKey: string;
  descriptionKey: string;
}> = [
  {
    icon: ShoppingCart,
    titleKey: 'landing.pillars.smartSalesTitle',
    descriptionKey: 'landing.pillars.smartSalesDescription',
  },
  {
    icon: MessageCircle,
    titleKey: 'landing.pillars.chatTitle',
    descriptionKey: 'landing.pillars.chatDescription',
  },
  {
    icon: PackageSearch,
    titleKey: 'landing.pillars.stockTitle',
    descriptionKey: 'landing.pillars.stockDescription',
  },
  {
    icon: Megaphone,
    titleKey: 'landing.pillars.growthTitle',
    descriptionKey: 'landing.pillars.growthDescription',
  },
  {
    icon: Smartphone,
    titleKey: 'landing.pillars.experienceTitle',
    descriptionKey: 'landing.pillars.experienceDescription',
  },
  {
    icon: Printer,
    titleKey: 'landing.pillars.counterTitle',
    descriptionKey: 'landing.pillars.counterDescription',
  },
];

const PAYMENT_FEATURE_KEYS = [
  'landing.payDelivery.paymentF1',
  'landing.payDelivery.paymentF2',
  'landing.payDelivery.paymentF3',
  'landing.payDelivery.paymentF4',
] as const;

const DELIVERY_FEATURE_KEYS = [
  'landing.payDelivery.deliveryF1',
  'landing.payDelivery.deliveryF2',
  'landing.payDelivery.deliveryF3',
  'landing.payDelivery.deliveryF4',
] as const;

const FINANCE_ITEMS: ReadonlyArray<{
  icon: LucideIcon;
  titleKey: string;
  descriptionKey: string;
}> = [
  {
    icon: CreditCard,
    titleKey: 'landing.finance.paymentsTitle',
    descriptionKey: 'landing.finance.paymentsDescription',
  },
  {
    icon: Landmark,
    titleKey: 'landing.finance.transfersTitle',
    descriptionKey: 'landing.finance.transfersDescription',
  },
  {
    icon: ShieldCheck,
    titleKey: 'landing.finance.kycTitle',
    descriptionKey: 'landing.finance.kycDescription',
  },
];

const INCLUDED_KEYS = [
  'landing.included.i1',
  'landing.included.i2',
  'landing.included.i3',
  'landing.included.i4',
  'landing.included.i5',
  'landing.included.i6',
  'landing.included.i7',
  'landing.included.i8',
] as const;

const DEMO_STORES: ReadonlyArray<{
  slug: string;
  icon: LucideIcon;
  labelKey: string;
  descriptionKey: string;
}> = [
  {
    slug: 'template-farmacia',
    icon: Pill,
    labelKey: 'landing.demos.farmacia',
    descriptionKey: 'landing.demos.farmaciaDescription',
  },
  {
    slug: 'template-moda',
    icon: Shirt,
    labelKey: 'landing.demos.moda',
    descriptionKey: 'landing.demos.modaDescription',
  },
  {
    slug: 'template-restaurante',
    icon: UtensilsCrossed,
    labelKey: 'landing.demos.restaurante',
    descriptionKey: 'landing.demos.restauranteDescription',
  },
];

export function MarketingLandingPage({
  t,
  normalizedLocale,
}: MarketingLandingPageProps) {
  const ecosystemCards: ReadonlyArray<{
    icon: LucideIcon;
    titleKey: string;
    descriptionKey: string;
    ctaKey: string;
    href: string;
  }> = [
    {
      icon: Handshake,
      titleKey: 'landing.ecosystem.affiliateTitle',
      descriptionKey: 'landing.ecosystem.affiliateDescription',
      ctaKey: 'landing.ecosystem.affiliateCta',
      href: `/${normalizedLocale}/affiliate-panel/login`,
    },
    {
      icon: Blocks,
      titleKey: 'landing.ecosystem.developerTitle',
      descriptionKey: 'landing.ecosystem.developerDescription',
      ctaKey: 'landing.ecosystem.developerCta',
      href: `/${normalizedLocale}/developer-panel/login`,
    },
    {
      icon: Plug,
      titleKey: 'landing.ecosystem.integrationsTitle',
      descriptionKey: 'landing.ecosystem.integrationsDescription',
      ctaKey: 'landing.ecosystem.integrationsCta',
      href: `/${normalizedLocale}/login`,
    },
  ];

  const tickerItems = TICKER_KEYS.map((key) => t(key));

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,color-mix(in_oklch,var(--primary)_9%,transparent),transparent_65%)]" />
        <div
          aria-hidden
          className="absolute -left-24 top-16 h-72 w-72 rounded-full bg-primary/10 blur-3xl animate-pulse [animation-duration:7s]"
        />
        <div
          aria-hidden
          className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-primary/10 blur-3xl animate-pulse [animation-duration:9s]"
        />

        <div className="container relative mx-auto max-w-7xl px-4 py-24 text-center sm:px-6 md:py-32 lg:px-8 animate-in fade-in-0 slide-in-from-bottom-4 duration-700">
          <p className="mb-8 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-primary">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
            </span>
            {t('landing.hero.eyebrow')}
          </p>

          <h1 className="mx-auto mb-6 max-w-4xl text-balance text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl">
            {t('landing.hero.title')}
            <br />
            <span className="text-primary">{t('landing.hero.titleHighlight')}</span>
          </h1>

          <p className="mx-auto mb-10 max-w-2xl text-balance text-lg leading-relaxed text-muted-foreground md:text-xl">
            {t('landing.hero.description')}
          </p>

          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="shadow-lg shadow-primary/25 transition-shadow hover:shadow-xl hover:shadow-primary/30">
              <Link href={`/${normalizedLocale}/login`}>
                {t('landing.hero.primaryCta')}
                <ArrowRight />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link href="#demos">{t('landing.hero.secondaryCta')}</Link>
            </Button>
          </div>

          <ul className="mt-8 flex flex-col items-center justify-center gap-x-8 gap-y-2 text-sm text-muted-foreground sm:flex-row">
            {(['landing.hero.point1', 'landing.hero.point2', 'landing.hero.point3'] as const).map(
              (key) => (
                <li key={key} className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                  {t(key)}
                </li>
              ),
            )}
          </ul>
        </div>
      </section>

      {/* Feature ticker */}
      <section aria-label={t('landing.ticker.label')} className="overflow-hidden border-b bg-secondary/50 py-4">
        <div className="flex w-max animate-landing-marquee gap-3 pr-3">
          {[false, true].map((duplicate) => (
            <div
              key={duplicate ? 'dup' : 'main'}
              aria-hidden={duplicate}
              className="flex shrink-0 items-center gap-3"
            >
              {tickerItems.map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border bg-card px-4 py-1.5 text-sm font-medium text-foreground"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  {item}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Three layers */}
      <section className="border-b">
        <div className="container mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <h2 className="mb-3 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              {t('landing.layers.title')}
            </h2>
            <p className="text-lg text-muted-foreground">{t('landing.layers.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8">
            {LAYERS.map((layer, index) => (
              <div
                key={layer.number}
                className={`border-t-2 border-primary/60 pt-6 animate-in fade-in-0 slide-in-from-bottom-4 duration-500 ${CARD_ENTRANCE_DELAYS[index]}`}
              >
                <span className="text-sm font-semibold tabular-nums text-primary">
                  {layer.number}
                </span>
                <h3 className="mb-2 mt-2 text-xl font-semibold text-foreground">
                  {t(layer.titleKey)}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {t(layer.descriptionKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Merchant pillars */}
      <section className="border-b bg-secondary/50">
        <div className="container mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <h2 className="mb-3 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              {t('landing.pillars.title')}
            </h2>
            <p className="text-lg text-muted-foreground">{t('landing.pillars.description')}</p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {PILLARS.map((pillar, index) => {
              const Icon = pillar.icon;
              return (
                <Card
                  key={pillar.titleKey}
                  className={`group h-full border bg-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-card animate-in fade-in-0 slide-in-from-bottom-4 duration-500 ${CARD_ENTRANCE_DELAYS[index]}`}
                >
                  <CardContent className="p-6">
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 transition-transform duration-300 group-hover:scale-110">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="mb-2 text-base font-semibold text-foreground">
                      {t(pillar.titleKey)}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {t(pillar.descriptionKey)}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Payment gateway + delivery spotlight */}
      <section className="border-b">
        <div className="container mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <h2 className="mb-3 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              {t('landing.payDelivery.title')}
            </h2>
            <p className="text-lg text-muted-foreground">{t('landing.payDelivery.description')}</p>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card className="group relative h-full overflow-hidden border bg-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-card">
              <div
                aria-hidden
                className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/10 blur-2xl transition-opacity duration-300 group-hover:opacity-100 opacity-60"
              />
              <CardContent className="relative p-8">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 transition-transform duration-300 group-hover:scale-110">
                  <QrCode className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-foreground">
                  {t('landing.payDelivery.paymentTitle')}
                </h3>
                <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
                  {t('landing.payDelivery.paymentDescription')}
                </p>
                <ul className="space-y-3">
                  {PAYMENT_FEATURE_KEYS.map((key) => (
                    <li key={key} className="flex items-start gap-3 text-sm text-foreground">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      {t(key)}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="group relative h-full overflow-hidden border bg-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-card">
              <div
                aria-hidden
                className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/10 blur-2xl transition-opacity duration-300 group-hover:opacity-100 opacity-60"
              />
              <CardContent className="relative p-8">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 transition-transform duration-300 group-hover:scale-110">
                  <Truck className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-foreground">
                  {t('landing.payDelivery.deliveryTitle')}
                </h3>
                <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
                  {t('landing.payDelivery.deliveryDescription')}
                </p>
                <ul className="space-y-3">
                  {DELIVERY_FEATURE_KEYS.map((key) => (
                    <li key={key} className="flex items-start gap-3 text-sm text-foreground">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      {t(key)}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Finance */}
      <section className="border-b bg-secondary/50">
        <div className="container mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
          <div className="overflow-hidden rounded-2xl border bg-card p-8 text-card-foreground md:p-14">
            <div className="mb-12 max-w-2xl">
              <h2 className="mb-3 text-3xl font-bold tracking-tight md:text-4xl">
                {t('landing.finance.title')}
              </h2>
              <p className="text-lg text-muted-foreground">{t('landing.finance.description')}</p>
            </div>
            <div className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8">
              {FINANCE_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.titleKey}>
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="mb-2 text-base font-semibold">{t(item.titleKey)}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {t(item.descriptionKey)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Everything included */}
      <section className="border-b">
        <div className="container mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <h2 className="mb-3 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              {t('landing.included.title')}
            </h2>
            <p className="text-lg text-muted-foreground">{t('landing.included.description')}</p>
          </div>
          <ul className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
            {INCLUDED_KEYS.map((key) => (
              <li
                key={key}
                className="flex items-start gap-3 rounded-lg border bg-card p-4 text-sm text-foreground transition-colors duration-200 hover:border-primary/40"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                {t(key)}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Demo stores */}
      <section id="demos" className="scroll-mt-8 border-b bg-secondary/50">
        <div className="container mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <h2 className="mb-3 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              {t('landing.demos.title')}
            </h2>
            <p className="text-lg text-muted-foreground">{t('landing.demos.description')}</p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {DEMO_STORES.map((store) => {
              const Icon = store.icon;
              return (
                <Link
                  key={store.slug}
                  href={`/${normalizedLocale}/${store.slug}`}
                  className="group block h-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  <Card className="h-full border bg-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-card">
                    <CardContent className="flex h-full flex-col p-6">
                      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 transition-transform duration-300 group-hover:scale-110">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="mb-2 flex items-center justify-between gap-2 text-base font-semibold text-foreground">
                        {t(store.labelKey)}
                        <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
                      </h3>
                      <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
                        {t(store.descriptionKey)}
                      </p>
                      <span className="mt-auto text-sm font-medium text-primary">
                        {t('landing.demos.visit')}
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ecosystem */}
      <section className="border-b">
        <div className="container mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <h2 className="mb-3 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              {t('landing.ecosystem.title')}
            </h2>
            <p className="text-lg text-muted-foreground">{t('landing.ecosystem.description')}</p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {ecosystemCards.map((card) => {
              const Icon = card.icon;
              return (
                <Card
                  key={card.titleKey}
                  className="h-full border bg-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-card"
                >
                  <CardContent className="flex h-full flex-col p-6">
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="mb-2 text-base font-semibold text-foreground">
                      {t(card.titleKey)}
                    </h3>
                    <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
                      {t(card.descriptionKey)}
                    </p>
                    <div className="mt-auto">
                      <Button asChild variant="outline" size="sm">
                        <Link href={card.href}>
                          {t(card.ctaKey)}
                          <ArrowRight />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,color-mix(in_oklch,var(--primary)_9%,transparent),transparent_65%)]" />
        <div
          aria-hidden
          className="absolute left-1/2 top-1/2 h-72 w-[36rem] max-w-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl animate-pulse [animation-duration:8s]"
        />
        <div className="container relative mx-auto max-w-7xl px-4 py-24 text-center sm:px-6 md:py-32 lg:px-8">
          <h2 className="mx-auto mb-6 max-w-3xl text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            {t('landing.cta.title')}
          </h2>
          <p className="mx-auto mb-10 max-w-2xl text-balance text-lg leading-relaxed text-muted-foreground">
            {t('landing.cta.description')}
          </p>
          <Button asChild size="lg" className="shadow-lg shadow-primary/25 transition-shadow hover:shadow-xl hover:shadow-primary/30">
            <Link href={`/${normalizedLocale}/login`}>
              {t('landing.cta.button')}
              <ArrowRight />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
